/**
 * Analogous to FieldEquationValidator.java
 */

import _ from 'lodash'
import Dataset from 'scoreboard/datasets/model/dataset'
import constants from 'scoreboard/components/constants'
const { TOTAL } = Dataset
const DatasetFieldType = constants.DATASET_FIELD_TYPE

/**
 * True if the equation part needs an aggregation, false otherwise. This logic is
 * server-side in FieldEquationValidator.java Please keep in sync with that file.
 * @param {Dataset} [startingDataset] the model of the initial dataset that was being edited
 * the field-equation-form.js was opened
 * @param {FieldEquationFormReferenceData} [referenceData]
 * @returns {boolean}
 *
 * Keep this in sync with FieldEquationValidator.java
 */
function needsAggregation(startingDataset, referenceData = {}) {
	if (_.isNil(referenceData.datasetId)) return false

	const isSameDataset = startingDataset?.id === referenceData.datasetId
	// if this is the same dataset, and there's no calculated field specified, we must
	// be dealing with a column from the source data or a basic field, in which case
	// aggregations are not needed
	const hasField = !_.isNil(referenceData.field) || !_.isNil(referenceData.fieldId)
	if (isSameDataset && !hasField) return false

	const referenceIsField = isBasicFieldNeedingAggOrIsLinkedDataset(startingDataset, referenceData.datasetId)
	// if we're referring to the Number of <tracked> or Total <tracked> "field"
	// in a linked dataset or if we are not working with a startingDataset
	// then we need an aggregation for the total "field"
	if (referenceIsField && referenceData.fieldId === TOTAL) return true

	// if this is a field from a linked dataset or if we have no startingDataset and
	// can pick any field from any dataset, we need an aggregation
	const isLinkedField = !isSameDataset
	if (isLinkedField || referenceIsField) return true

	// if this is a calculated field from the same dataset, then we need an aggregation
	return isNonBasicFieldFromSameDataset(startingDataset, referenceData)
}

/**
 * True if the equation part is a NOT a basic field (single column from source data)
 * from the same dataset, false otherwise
 * @param {Dataset} [startingDataset] the model of the initial dataset that was being edited
 * when the field-equation-form.js was opened
 * @param {FieldEquationFormReferenceData} [referenceData]
 * @returns {boolean}
 *
 * Keep this in sync with FieldEquationValidator.java
 */
function isNonBasicFieldFromSameDataset(startingDataset, referenceData = {}) {
	const isSameDataset = startingDataset?.id === referenceData.datasetId
	if (!isSameDataset) return false

	let field = Dataset.getFieldsByLabel(startingDataset)[referenceData.field]
	if (_.isNil(field)) {
		field = startingDataset?.combinedFieldsById[referenceData.fieldId]
	}
	if (_.isNil(field)) return false

	return field.fieldType !== DatasetFieldType.BASIC
}

/**
 * Evaluates to true for any of the following conditions:
 * - the startingDataset is null
 * - the startingDataset.id and datasetId are the same
 * - the datasetId is an id of one of startingDataset's linked datasets
 *
 * Evaluates to false if equationDatasetId is null
 *
 * @param startingDataset the starting dataset, if one was provided from the UI
 * @param equationDatasetId the datasetId from the equation i.e.
 * [datasetId].[field label]
 * Keep this in sync with FieldEquationValidator.java
 */
function isBasicFieldNeedingAggOrIsLinkedDataset(startingDataset, datasetId) {
	if (startingDataset === null || startingDataset?.id === datasetId) return true

	const linkedDataset = startingDataset.datasetsById[datasetId]
	return linkedDataset !== null
}

export default {
	needsAggregation,
	isNonBasicFieldFromSameDataset,
	isBasicFieldNeedingAggOrIsLinkedDataset
}